import React, { useState, useCallback } from 'react';
import AdBanner from '../components/AdBanner';
import { UploadCloud, Image as ImageIcon, Loader2, Download as DownloadIcon, Lock as LockIcon, CheckCircle, Sparkles, RefreshCw, Zap, X } from 'lucide-react';
import { processImage } from '../services/api';
import HomeContent from '../components/HomeContent';
const Home = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [copies, setCopies] = useState(4);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasRemovedBg, setHasRemovedBg] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);
  const [adCompleted, setAdCompleted] = useState(false);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setupFile(e.target.files[0]);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setupFile(e.dataTransfer.files[0]);
    }
  }, []);
  const handleWatchAdToUnlockHD = () => {
    setShowAdModal(true);
    setAdCompleted(false); // reset on open
    
    // Simulate Ad duration (15 seconds) before unlocking
    setTimeout(() => {
      setAdCompleted(true);
    }, 15000);
  };

  const closeAdModalAndDownloadHD = () => {
    setShowAdModal(false);
    if (result && result.png) {
      window.open(result.png, '_blank');
    }
  };

  const setupFile = (selectedFile) => {
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
    setResult(null);
    setError(null);
    setHasRemovedBg(false);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleSubmit = async (e, forceRemoveBg = false) => {
    if (e) e.preventDefault();
    if (!file) return;

    setLoading(true);
    setError(null);
    setLoadingStep('Uploading & Processing...');
    
    const formData = new FormData();
    formData.append('image', file);
    formData.append('copies', copies);
    
    if (forceRemoveBg) {
      formData.append('removeBg', 'true');
    }

    try {
      if (forceRemoveBg) {
        setTimeout(() => setLoadingStep('AI Removing Background (This might take 10s)...'), 1500);
      } else {
        setTimeout(() => setLoadingStep('Formatting Grid...'), 1500);
      }

      const data = await processImage(formData);
      setResult(data.downloads);
      if (forceRemoveBg) setHasRemovedBg(true);
    } catch (err) {
      console.error(err);
      setError(err.error || 'Something went wrong during processing.');
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-12">
  {/*<AdBanner slotId="2888628999801486" />*/}

      <div className="text-center space-y-6 max-w-3xl mx-auto pt-4 pb-8">
        <h1 className="text-5xl md:text-[4rem] font-extrabold text-slate-900 tracking-tight leading-tight mix-blend-multiply">
          Perfect Passport Photos <br/>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 animate-gradient-x">
            Powered by AI
          </span>
        </h1>
        <p className="text-lg md:text-xl text-slate-600 drop-shadow-sm font-medium mb-4">
          Upload any picture and let our state-of-the-art AI remove the background, enhance quality, and format it exactly to standard passport dimensions.
        </p>
        <div className="flex items-center justify-center space-x-4 text-sm font-bold text-slate-600 pt-3">
           <span className="flex items-center bg-white px-4 py-1.5 rounded-full shadow-sm border border-slate-100 transition-transform hover:scale-105"><CheckCircle className="w-4 h-4 text-emerald-500 mr-2" /> 100% Free Format</span>
           <span className="flex items-center bg-white px-4 py-1.5 rounded-full shadow-sm border border-slate-100 transition-transform hover:scale-105"><CheckCircle className="w-4 h-4 text-emerald-500 mr-2" /> Instant AI Generation</span>
        </div>
      </div>
  <HomeContent />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        <div className="lg:col-span-5 flex flex-col space-y-6">
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 flex flex-col space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center text-slate-800">
                <UploadCloud className="mr-3 text-indigo-500 w-6 h-6" /> Image Source
              </h2>
            </div>
            
            <div 
              className={`group relative border-[3px] border-dashed rounded-3xl p-10 flex flex-col items-center justify-center transition-all duration-500 cursor-pointer overflow-hidden ${isDragging ? 'border-indigo-500 bg-indigo-50/80 shadow-[inset_0_0_80px_rgba(79,70,229,0.15)] scale-[1.02]' : 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 bg-slate-50/50 hover:shadow-xl'} ${preview ? 'h-72' : 'h-72'}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Animated background glow on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

              <input 
                type="file" 
                accept="image/*" 
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                onChange={handleFileChange}
              />
              {!preview ? (
                <div className="text-center flex flex-col items-center pointer-events-none transform group-hover:-translate-y-2 transition-all duration-500 ease-out z-10">
                  <div className="bg-white p-5 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.05)] mb-6 ring-1 ring-slate-100 group-hover:shadow-[0_15px_40px_rgba(79,70,229,0.15)] group-hover:ring-indigo-100 transition-all duration-500">
                    <ImageIcon className="w-10 h-10 text-indigo-500 group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <h3 className="text-slate-800 font-extrabold text-xl mb-1">Upload Photo Here</h3>
                  <p className="text-sm text-slate-500 font-medium">Drag & drop or <span className="text-indigo-600 underline decoration-indigo-300 underline-offset-2">browse files</span></p>
                  <div className="mt-6 flex space-x-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100 px-3 py-1 rounded-md">
                    <span>JPEG</span><span>•</span><span>PNG</span><span>•</span><span>HEIC</span>
                  </div>
                </div>
              ) : (
                <div className="relative w-full h-full group">
                  <img src={preview} alt="Upload preview" className="w-full h-full object-contain rounded-lg shadow-sm" />
                  <div className="absolute inset-0 bg-slate-900/40 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                    <span className="bg-white text-slate-800 px-4 py-2 rounded-full font-bold text-sm shadow-lg flex items-center">
                      <RefreshCw className="w-4 h-4 mr-2" /> Change Photo
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-bold text-slate-700">Grid Format Options</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { count: 2, label: '2 Copies', desc: 'Standard usage' },
                  { count: 4, label: '4 Copies', desc: 'Most popular' },
                  { count: 6, label: '6 Copies', desc: 'Extra spares' },
                  { count: 8, label: '8 Copies', desc: 'Family pack' }
                ].map(opt => (
                  <button
                    key={opt.count}
                    onClick={() => setCopies(opt.count)}
                    className={`relative p-3 rounded-xl border-2 text-left transition-all duration-200 ${copies === opt.count ? 'border-indigo-600 bg-indigo-50/50 shadow-sm' : 'border-slate-100 bg-white hover:border-indigo-200 hover:bg-slate-50'}`}
                  >
                    {copies === opt.count && <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-indigo-600"></div>}
                    <div className={`font-bold ${copies === opt.count ? 'text-indigo-900' : 'text-slate-700'}`}>{opt.label}</div>
                    <div className={`text-xs mt-0.5 ${copies === opt.count ? 'text-indigo-600 font-medium' : 'text-slate-500'}`}>{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={!file || loading}
              className="w-full group relative overflow-hidden bg-slate-900 hover:bg-indigo-600 disabled:bg-slate-300 text-white font-bold py-4 px-6 rounded-2xl flex justify-center items-center transition-all duration-300 shadow-[0_0_10px_rgba(0,0,0,0.1)] hover:shadow-[0_0_20px_rgba(79,70,229,0.3)] active:scale-[0.98]"
            >
              <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-3 h-5 w-5" /> Processing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-5 w-5 text-indigo-300 group-hover:text-white transition-colors" /> Generate Photos
                </>
              )}
            </button>

            {error && (
              <div className="animate-fade-in-up bg-red-50 border border-red-100 p-4 rounded-xl text-center">
                <p className="text-red-500 text-sm font-semibold">{error}</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col h-full">
            
          
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 flex-grow flex flex-col relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50 rounded-full blur-3xl opacity-60 -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>

            <h2 className="text-xl font-bold mb-8 flex items-center text-slate-800 z-10 border-b border-slate-100 pb-4">
               <Zap className="text-amber-500 mr-2 w-6 h-6 fill-amber-500" /> Output Results
            </h2>

            <div className="flex-grow flex flex-col justify-center relative z-10">
              {loading && (
                <div className="w-full max-w-md mx-auto flex flex-col items-center justify-center space-y-6">
                  <div className="relative">
                     <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                     <div className="absolute inset-0 flex items-center justify-center">
                       <Sparkles className="w-5 h-5 text-indigo-500 animate-pulse" />
                     </div>
                  </div>
                  <div className="text-center space-y-2">
                    <p className="text-lg font-bold text-slate-800">Hang tight!</p>
                    <p className="text-indigo-600 font-medium animate-pulse">{loadingStep}</p>
                  </div>
{  /*   <div className="w-full mt-6 bg-slate-50 p-4 rounded-xl border border-slate-100 shadow-inner">
                    <p className="text-xs text-slate-400 font-semibold mb-2 uppercase tracking-wider text-center">Sponsor Message</p>
                    <AdBanner slotId="2888628999801486" />
                  </div>}*/}
                </div>
              )}

              {!loading && !result && (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <div className="bg-slate-50 p-6 rounded-full mb-6">
                    <ImageIcon className="w-12 h-12 text-slate-300" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-700 mb-2">Awaiting Image</h3>
                  <p className="text-slate-500 max-w-sm">Upload a photo to see the generated passport size templates directly here.</p>
                </div>
              )}

              {!loading && result && (
                <div className="animate-fade-in-up space-y-8">
                  <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-2xl flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-emerald-800 font-bold">Successfully Generated!</h4>
                      <p className="text-emerald-600 text-sm font-medium mt-1">Your grid is perfectly formatted.</p>
                    </div>
                  </div>

                  {!hasRemovedBg && (
                    <div className="bg-indigo-50 border border-indigo-100 p-5 rounded-2xl flex flex-col sm:flex-row items-center justify-between sm:space-x-3 shadow-sm">
                      <div className="text-center sm:text-left mb-4 sm:mb-0">
                        <h4 className="text-indigo-900 font-bold flex items-center justify-center sm:justify-start">
                          <Sparkles className="w-4 h-4 mr-2 text-indigo-600" /> AI Background Eraser
                        </h4>
                        <p className="text-indigo-600/80 text-xs font-medium mt-1">Does your image have a messy background? Let AI remove it.</p>
                      </div>
                      <button 
                        onClick={(e) => handleSubmit(e, true)}
                        className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-5 rounded-xl shadow-md transition-colors whitespace-nowrap text-sm"
                      >
                        Remove Background
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4">
                    <div className="p-6 border-2 border-slate-100 rounded-2xl bg-white hover:border-indigo-100 transition-colors group flex flex-col sm:flex-row items-center justify-between shadow-sm hover:shadow-md">
                       <div className="text-center sm:text-left mb-4 sm:mb-0">
                          <h3 className="font-bold text-slate-800 text-lg">Standard Download</h3>
                          <p className="text-sm text-slate-500 font-medium mt-1">Ready to print format • <span className="text-emerald-600 font-bold">Free</span></p>
                       </div>
                       <div className="flex flex-wrap justify-center sm:justify-end gap-3 w-full sm:w-auto">
                         <a href={result.png} target="_blank" rel="noopener noreferrer" download className="py-2.5 px-4 rounded-xl flex items-center transition-colors bg-indigo-50 hover:bg-indigo-600 hover:text-white text-indigo-700 font-semibold justify-center shadow-sm">
                           <DownloadIcon className="w-4 h-4 mr-2" /> Image (PNG)
                         </a>
                         <a href={result.pdf} target="_blank" rel="noopener noreferrer" download className="py-2.5 px-4 rounded-xl flex items-center transition-colors bg-rose-50 hover:bg-rose-600 hover:text-white text-rose-700 font-semibold justify-center shadow-sm">
                           <DownloadIcon className="w-4 h-4 mr-2" /> PDF Print
                         </a>
                         {result.docx && (
                           <a href={result.docx} target="_blank" rel="noopener noreferrer" download className="py-2.5 px-4 rounded-xl flex items-center transition-colors bg-blue-50 hover:bg-blue-600 hover:text-white text-blue-700 font-semibold justify-center shadow-sm">
                             <DownloadIcon className="w-4 h-4 mr-2" /> Word (DOC)
                           </a>
                         )}
                       </div>
                    </div>

                    <div className="p-1 border border-indigo-200 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 rounded-2xl shadow-lg relative overflow-hidden">
                      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                      <div className="bg-slate-900 rounded-xl p-5 md:p-6 flex flex-col md:flex-row items-center justify-between relative z-10 w-full h-full">
                        <div className="text-center md:text-left mb-4 md:mb-0">
                          <div className="flex justify-center md:justify-start items-center space-x-2">
                             <h3 className="font-bold text-white text-lg flex items-center mb-1">HD Quality Package</h3>
                             <span className="bg-indigo-500/30 text-indigo-300 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold border border-indigo-500/50">Pro</span>
                          </div>
                          <p className="text-sm text-slate-400 font-medium">Uncompressed max resolution</p>
                        </div>
                        <button 
                          onClick={handleWatchAdToUnlockHD}
                          className="w-full md:w-auto bg-white text-slate-900 hover:bg-indigo-50 font-bold py-3 px-5 rounded-xl shadow transition-transform duration-200 hover:scale-105 flex justify-center items-center group">
                          <LockIcon className="w-4 h-4 mr-2 text-indigo-500 group-hover:text-indigo-600" /> Unlock HD
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto pb-8">
                         {/*   <AdBanner slotId="2888628999801486" />*/}
      </div>

      {/* HD Ad Modal placeholder */}
      {showAdModal && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-slate-100 overflow-hidden relative animate-fade-in-up">
            
            {/* Header */}
            <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-slate-50/50">
               <h3 className="font-bold text-slate-800 flex items-center">
                 <Sparkles className="w-5 h-5 text-indigo-500 mr-2" /> HD Quality Unlock
               </h3>
               <button 
                 onClick={() => setShowAdModal(false)}
                 className="p-1.5 hover:bg-slate-200 rounded-full transition-colors text-slate-500"
               >
                 <X className="w-5 h-5" />
               </button>
            </div>

       
            {/* Ad Placeholder Body */}
         //   <div className="p-8 text-center bg-slate-50">
            //   <p className="text-slate-600 font-medium mb-6">Please support us by viewing a short sponsor message to unlock the Uncompressed HD max resolution.</p>
               
             //  <div className="w-full h-64 bg-slate-200 rounded-2xl border-2 border-dashed border-slate-300 flex items-center justify-center relative overflow-hidden mb-6 group">
             //    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:animate-[shimmer_2s_infinite]"></div>
       {/*<AdBanner slotId="2888628999801486" />*/}
            //   </div>
        //    </div>
           {/* Footer action */}
            <div className="p-5 border-t border-slate-100 bg-white flex justify-end items-center transition-all h-[80px]">
               {!adCompleted ? (
                 <div className="flex items-center text-slate-500 font-bold px-6 py-3 cursor-not-allowed opacity-0"></div>
               ) : (
                 <button 
                    onClick={closeAdModalAndDownloadHD}
                    className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-md active:scale-95 animate-fade-in-up"
                  >
                    <DownloadIcon className="w-4 h-4 mr-2" /> Download HD Photo
                 </button>
               )}
            </div>
          </div>
        </div>
      )}

    <div className="bg-white p-10 rounded-3xl shadow-sm border border-slate-100 mt-12 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-slate-800 mb-6">How to Take a Perfect Passport Photo at Home</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <p className="text-slate-600 mb-4">Follow these simple steps to ensure your photo is accepted by official agencies:</p>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-indigo-500 mr-2 mt-1" /> <strong>Lighting:</strong> Use natural light by facing a window to avoid face shadows.</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-indigo-500 mr-2 mt-1" /> <strong>Background:</strong> Use a plain white or off-white wall as your backdrop.</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-indigo-500 mr-2 mt-1" /> <strong>Position:</strong> Keep the camera at eye level, approximately 3-4 feet away.</li>
            </ul>
          </div>
          
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-600 font-bold">
                <tr>
                  <th className="px-4 py-3">Document Type</th>
                  <th className="px-4 py-3">Official Size</th>
                </tr>
              </thead>
              <tbody className="text-slate-700 divide-y divide-slate-100">
                <tr>
                  <td className="px-4 py-3 font-medium">Indian Passport</td>
                  <td className="px-4 py-3">3.5 x 4.5 cm</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">PAN Card</td>
                  <td className="px-4 py-3">2.5 x 3.5 cm</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Voter ID</td>
                  <td className="px-4 py-3">2.0 x 2.5 cm</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p className="mt-6 text-sm text-slate-400 italic text-center">
          Note: Our AI tool automatically handles these dimensions for you.
        </p>
      </div>
      {/* --- FAQ SECTION FOR SEO & USER TRUST --- */}
<div className="bg-slate-50 p-10 rounded-3xl border border-slate-100 mt-12 max-w-4xl mx-auto mb-20">
  <h2 className="text-2xl font-bold text-slate-800 mb-8 text-center">Frequently Asked Questions</h2>
  
  <div className="space-y-6 text-left">
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
      <h3 className="font-bold text-indigo-600 mb-2">Q: Is this tool really free?</h3>
      <p className="text-slate-600 text-sm">Yes! You can generate and download standard-quality passport photo grids for free. We offer an HD option for users who want maximum resolution.</p>
    </div>

    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
      <h3 className="font-bold text-indigo-600 mb-2">Q: Can I use these photos for an Indian Passport or Visa?</h3>
      <p className="text-slate-600 text-sm">Yes. Our tool follows the official 3.5 x 4.5 cm (Passport) and 2 x 2 inch (Visa) requirements. Just ensure your background is plain white as per government rules.</p>
    </div>

    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
      <h3 className="font-bold text-indigo-600 mb-2">Q: How does the AI Background Remover work?</h3>
      <p className="text-slate-600 text-sm">Our AI detects the person in your photo and automatically replaces any messy background with a clean, official white background, saving you a trip to a professional studio.</p>
    </div>

    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
      <h3 className="font-bold text-indigo-600 mb-2">Q: What is the best way to print these photos?</h3>
      <p className="text-slate-600 text-sm">Download the PDF version for the best results. When printing, set your printer to <strong>"Actual Size"</strong> or <strong>"100% Scale"</strong> on a 4x6 inch (A6) photo paper.</p>
    </div>

    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
      <h3 className="font-bold text-indigo-600 mb-2">Q: Is my privacy protected?</h3>
      <p className="text-slate-600 text-sm">Privacy is our priority. Photos are processed temporarily to create your grid and are <strong>never stored</strong> on our servers after your session ends.</p>
    </div>
  </div>
  
  <p className="mt-8 text-center text-slate-400 text-xs">
    Developed by <span className="font-bold text-indigo-400">DarkCoder</span> • Supporting Digital India
  </p>
</div>
{/* ------------------------------------------ */}
  
    </div>
  );
};

export default Home;
